import { clientStorage } from "@/lib/clientStorage";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useCloudDrafts } from "@/hooks/useCloudDrafts";
import { useAuth } from "@/lib/AuthContext";
import { 
  FolderOpen, 
  Trash2, 
  RefreshCw,
  FileText,
  Cloud,
  Monitor,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const FORM_TYPE_TABS = {
  "certification_sheet": "certification",
  "legal_aid_form": "aid-form",
  "backsheet": "backsheet",
  "travel_claim": "travel-claim"
};

const FORM_LABELS = {
  "certification_sheet": "Guideline compliance certification",
  "legal_aid_form": "Legal Aid Application",
  "backsheet": "Backsheet to Counsel",
  "travel_claim": "Travel Claim Form"
};

const BRAND_DETAILS = {
  eas: {
    orgName: "EAS Legal",
    appPrefix: "EAS",
    textColor: "text-amber-600",
    bgHeader: "bg-gradient-to-r from-amber-800 to-amber-700",
    bgButton: "bg-amber-650 hover:bg-amber-700 focus:ring-amber-500",
    borderColor: "border-amber-100",
    accentColor: "#d97706"
  },
  vals: {
    orgName: "Victorian Aboriginal Legal Service",
    appPrefix: "VALS",
    textColor: "text-rose-700",
    bgHeader: "bg-gradient-to-r from-rose-800 to-rose-700",
    bgButton: "bg-rose-700 hover:bg-rose-800 focus:ring-rose-500",
    borderColor: "border-rose-100",
    accentColor: "#be123c"
  },
  lacw: {
    orgName: "Law & Advocacy Centre for Women",
    appPrefix: "LACW",
    textColor: "text-purple-700",
    bgHeader: "bg-gradient-to-r from-purple-800 to-purple-700",
    bgButton: "bg-purple-700 hover:bg-purple-800 focus:ring-purple-500",
    borderColor: "border-purple-100",
    accentColor: "#7e22ce"
  },
  jbl: {
    orgName: "Jarrod Behan Law",
    appPrefix: "JBL",
    textColor: "text-blue-700",
    bgHeader: "bg-gradient-to-r from-blue-800 to-blue-700",
    bgButton: "bg-blue-700 hover:bg-blue-800 focus:ring-blue-500",
    borderColor: "border-blue-100",
    accentColor: "#1d4ed8"
  },
  rga: {
    orgName: "Robyn Greensill & Associates",
    appPrefix: "RGA",
    textColor: "text-teal-700",
    bgHeader: "bg-gradient-to-r from-teal-800 to-teal-700",
    bgButton: "bg-teal-700 hover:bg-teal-800 focus:ring-teal-500",
    borderColor: "border-teal-100",
    accentColor: "#0f766e"
  },
  counsel: {
    orgName: "Counsel Chambers",
    appPrefix: "Counsel",
    textColor: "text-slate-800",
    bgHeader: "bg-gradient-to-r from-slate-900 to-slate-800",
    bgButton: "bg-slate-850 hover:bg-slate-900 focus:ring-slate-500",
    borderColor: "border-slate-200",
    accentColor: "#f59e0b"
  },
  admin: {
    orgName: "Complete Law Support",
    appPrefix: "CLS",
    textColor: "text-blue-700",
    bgHeader: "bg-gradient-to-r from-blue-800 to-blue-700",
    bgButton: "bg-blue-700 hover:bg-blue-800 focus:ring-blue-500",
    borderColor: "border-blue-100",
    accentColor: "#1d4ed8"
  }
};

export default function DraftForms({ onTabChange, brandType = "vals" }) {
  const { user } = useAuth();
  const { fetchDrafts, deleteDraft } = useCloudDrafts();
  const [allDrafts, setAllDrafts] = useState([]);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const brandObj = BRAND_DETAILS[brandType] || BRAND_DETAILS.vals;

  // Local storage brand prefix
  const brandKey = brandType === "admin" ? "" : `${brandType}_`;

  // Fetch and compile all local and cloud drafts
  const getLocalDrafts = () => {
    const list = [];
    try {
      // 1. VLA application drafts
      const vlaKey = `${brandKey}vla_drafts_list`;
      const vlaStr = clientStorage.getItem(vlaKey);
      if (vlaStr) {
        const drafts = JSON.parse(vlaStr);
        drafts.forEach(d => {
          list.push({
            id: d.id,
            form_type: "legal_aid_form",
            form_label: FORM_LABELS.legal_aid_form,
            client_name: d.applicant_name || "Unnamed Client",
            updated_at: d.updated_at,
            saved_by: d.saved_by || "Local Browser",
            storage_type: "local"
          });
        });
      }

      // 2. Guideline Certification drafts
      const certKey = `${brandKey}guideline_drafts_list`;
      const certStr = clientStorage.getItem(certKey);
      if (certStr) {
        const drafts = JSON.parse(certStr);
        drafts.forEach(d => {
          list.push({
            id: d.id,
            form_type: "certification_sheet",
            form_label: FORM_LABELS.certification_sheet,
            client_name: d.client_name || "Unnamed Client",
            updated_at: d.updated_at,
            saved_by: d.saved_by || "Local Browser",
            storage_type: "local"
          });
        });
      }

      // 3. Backsheet drafts
      const backsheetKey = `${brandKey}backsheet_drafts_list`;
      const backsheetStr = clientStorage.getItem(backsheetKey);
      if (backsheetStr) {
        const drafts = JSON.parse(backsheetStr);
        drafts.forEach(d => {
          list.push({
            id: d.id,
            form_type: "backsheet",
            form_label: FORM_LABELS.backsheet,
            client_name: d.client_name || d.matter_id || "Unnamed Draft",
            updated_at: d.updated_at,
            saved_by: d.saved_by || "Local Browser",
            storage_type: "local"
          });
        });
      }

      // 4. Travel Claim drafts
      const travelKey = `${brandKey}travel_claim_drafts_list`;
      const travelStr = clientStorage.getItem(travelKey);
      if (travelStr) {
        const drafts = JSON.parse(travelStr);
        drafts.forEach(d => {
          list.push({
            id: d.id,
            form_type: "travel_claim",
            form_label: FORM_LABELS.travel_claim,
            client_name: d.client_name || "Unnamed Draft",
            updated_at: d.updated_at,
            saved_by: d.saved_by || "Local Browser",
            storage_type: "local"
          });
        });
      }
    } catch (e) {
      console.error("Failed to fetch local drafts:", e);
    }
    return list;
  };

  const loadAllDrafts = async () => {
    setIsLoadingAll(true);
    try {
      const local = getLocalDrafts();
      const cloud = await fetchDrafts(null, brandType);
      
      const formattedCloud = (cloud || [])
        .filter(d => d.form_type !== "monthly_trust_receipts")
        .map(d => ({
          id: d.id,
          form_type: d.form_type,
          form_label: FORM_LABELS[d.form_type] || d.form_type,
          client_name: d.draft_name || "Unnamed Client",
          updated_at: d.updated_at || d.created_at,
          saved_by: d.saved_by || "Cloud User",
          storage_type: "cloud",
          raw_draft: d
        }));

      const combined = [...local, ...formattedCloud].sort(
        (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
      );
      setAllDrafts(combined);
    } catch (e) {
      toast.error("Failed to load drafts history.");
    } finally {
      setIsLoadingAll(false);
    }
  };

  useEffect(() => {
    loadAllDrafts();
  }, [brandType]);

  // Delete draft handler
  const handleDeleteDraft = async (draft) => {
    if (!window.confirm(`Are you sure you want to delete the draft for "${draft.client_name}"?`)) return;

    try {
      if (draft.storage_type === "cloud") {
        await deleteDraft(draft.id, null, brandType);
      } else {
        // Delete local VLA Application
        if (draft.form_type === "legal_aid_form") {
          const vlaKey = `${brandKey}vla_drafts_list`;
          const drafts = getLocalDrafts().filter(d => d.id !== draft.id);
          clientStorage.setItem(vlaKey, JSON.stringify(drafts.map(d => ({
            id: d.id,
            applicant_name: d.client_name,
            updated_at: d.updated_at
          }))));
          clientStorage.removeItem(`${brandKey}vla_application_${draft.id}`);
        }
        // Delete local Guideline Certification
        if (draft.form_type === "certification_sheet") {
          const certKey = `${brandKey}guideline_draft_list` || `${brandKey}guideline_drafts_list`;
          const drafts = getLocalDrafts().filter(d => d.id !== draft.id);
          clientStorage.setItem(certKey, JSON.stringify(drafts.map(d => ({
            id: d.id,
            client_name: d.client_name,
            updated_at: d.updated_at
          }))));
          clientStorage.removeItem(`${brandKey}guideline_draft_${draft.id}`);
        }
        // Delete local Backsheet
        if (draft.form_type === "backsheet") {
          const key = `${brandKey}backsheet_drafts_list`;
          const drafts = getLocalDrafts().filter(d => d.id !== draft.id);
          clientStorage.setItem(key, JSON.stringify(drafts.map(d => ({
            id: d.id,
            client_name: d.client_name,
            updated_at: d.updated_at
          }))));
          clientStorage.removeItem(`${brandKey}backsheet_draft_${draft.id}`);
        }
        // Delete local Travel Claim
        if (draft.form_type === "travel_claim") {
          const key = `${brandKey}travel_claim_drafts_list`;
          const drafts = getLocalDrafts().filter(d => d.id !== draft.id);
          clientStorage.setItem(key, JSON.stringify(drafts.map(d => ({
            id: d.id,
            client_name: d.client_name,
            updated_at: d.updated_at
          }))));
          clientStorage.removeItem(`${brandKey}travel_claim_draft_${draft.id}`);
        }
      }
      toast.success("Draft deleted successfully.");
      loadAllDrafts();
    } catch (e) {
      toast.error("Failed to delete draft.");
    }
  };

  // Restore/Open draft handler
  const handleOpenDraft = (draft) => {
    const tabId = FORM_TYPE_TABS[draft.form_type];
    if (!tabId) {
      toast.error("Unsupported form type for redirection.");
      return;
    }

    if (draft.storage_type === "cloud") {
      // For cloud draft, save it to local storage under the form prefix so VLA or Certification load it
      const payload = {
        id: draft.id,
        form_data: draft.raw_draft.form_data,
        applicant_name: draft.client_name,
        client_name: draft.client_name,
        status: "draft",
        updated_at: draft.updated_at
      };

      if (draft.form_type === "legal_aid_form") {
        const vlaDraftKeyPrefix = `${brandKey}vla_application_`;
        clientStorage.setItem(`${vlaDraftKeyPrefix}${draft.id}`, JSON.stringify(payload));
      } else if (draft.form_type === "certification_sheet") {
        const draftKeyPrefix = `${brandKey}guideline_draft_`;
        clientStorage.setItem(`${draftKeyPrefix}${draft.id}`, JSON.stringify(payload));
      } else if (draft.form_type === "backsheet") {
        const keyPrefix = `${brandKey}backsheet_draft_`;
        clientStorage.setItem(`${keyPrefix}${draft.id}`, JSON.stringify(payload));
      } else if (draft.form_type === "travel_claim") {
        const keyPrefix = `${brandKey}travel_claim_draft_`;
        clientStorage.setItem(`${keyPrefix}${draft.id}`, JSON.stringify(payload));
      }
    }

    // Switch tab and pass id in search parameter
    onTabChange(tabId, draft.id);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Title */}
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <FolderOpen className={`w-7 h-7 ${brandObj.textColor}`} />
              Draft Forms
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Access and manage all local browser drafts and shared cloud workspace drafts prepared for {brandObj.orgName}.
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadAllDrafts} 
            disabled={isLoadingAll}
            className="flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoadingAll ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Drafts List Card */}
        <Card className={`${brandObj.borderColor} shadow-sm`}>
          <CardHeader className={`${brandObj.bgHeader} text-white rounded-t-lg py-4`}>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Saved Drafts History
            </CardTitle>
            <CardDescription className="text-slate-100 text-xs">
              Review saved client drafts, date updated, and preparer details. Click Restore to resume editing.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {isLoadingAll ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <RefreshCw className="w-8 h-8 animate-spin text-slate-400 mb-3" />
                <p className="text-xs text-slate-500 font-semibold">Scanning local and cloud draft files...</p>
              </div>
            ) : allDrafts.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-16 text-center">
                <FolderOpen className="w-12 h-12 text-slate-350 mb-3" />
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">No Draft Forms Found</h3>
                <p className="text-xs text-slate-450 dark:text-slate-500 mt-1.5 max-w-sm leading-relaxed mx-auto">
                  There are no local or cloud-saved drafts currently filed for this portal. Saves will automatically show up here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100/75 dark:bg-slate-900 border-b border-slate-200/60 dark:border-slate-800 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      <th className="py-3.5 px-5">Client / Draft Name</th>
                      <th className="py-3.5 px-4">Form / Tool Type</th>
                      <th className="py-3.5 px-4">Prepared By</th>
                      <th className="py-3.5 px-4">Date Saved</th>
                      <th className="py-3.5 px-4">Location</th>
                      <th className="py-3.5 px-5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                    {allDrafts.map(draft => (
                      <tr 
                        key={`${draft.storage_type}_${draft.id}`}
                        className="hover:bg-slate-50/50 dark:hover:bg-slate-950/20 transition-colors"
                      >
                        <td className="py-4 px-5">
                          <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                            {draft.client_name}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-xs text-slate-600 dark:text-slate-400">
                            {draft.form_label}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-350">
                            {draft.saved_by}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {new Date(draft.updated_at).toLocaleDateString("en-AU", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit"
                            })}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          {draft.storage_type === "cloud" ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-50 dark:bg-blue-950/30 px-2 py-0.5 rounded-full">
                              <Cloud className="w-3 h-3" />
                              Cloud Draft
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                              <Monitor className="w-3 h-3" />
                              Local Browser
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs px-2.5 py-1 flex items-center gap-1"
                              onClick={() => handleOpenDraft(draft)}
                            >
                              Restore
                              <ArrowRight className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded"
                              onClick={() => handleDeleteDraft(draft)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
